import Icon, { DownOutlined, RightOutlined, CloseCircleOutlined, SearchOutlined } from '@ant-design/icons'
import { Table, message, Input } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import useToken from 'antd/es/theme/useToken'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ReactComponent as PatientIcon } from '../../assets/Icons_ Patient.svg'
import { ReactComponent as DashboardIconFilled } from '../../assets/Icons_Dashboard  fill.svg'
import CountdownButton from '../../components/CountdownButton/CountdownButton'
import FullScreenContainer from '../../components/FullScreenContainer/FullScreenContainer'
import HospitalStatsCard from '../../components/HospitalStatsCard/HospitalStatsCard'
import { displayMessage } from '../../helpers/Message'
import { useGetTotalAlertsLast12hQuery } from '../../state/alerts/api'
import { getPatients } from '../../state/patient/patientReducer'
import {
  errorPatientAdmissions,
  getPatientAdmissions,
  selectAllPatientAdmissions,
  selectErrorInPatientAdmissions,
  selectPatientAdmissionLoadingState,
} from '../../state/patientAdmission/patientAdmissionReducer'
import { AssignedDoctorNameType } from '../../state/patientAdmission/types'
import { useGetPatientAdmissionsMewsScoreDataQuery } from '../../state/patientAdmissionMews/api'
import '../../styles/antd/tableStyle.css'
import { PatientAdmissionViewDataType } from '../../types/PatientViewDataType'
import Header from '../CyHeader/CyHeader'
import { PatientTabs } from '../PatientsView/PatientTabs'
import {
  EcgStatus,
  MewsScore,
  PatientContext,
  VitalStatus,
} from '../PatientsView/PatientsView'
import {
  DashboardContainer,
  DashboardPatientAdmissionContainer,
  DashboardStatsContainer,
  TableTitle,
  TableTitleDiv,
  TableTitleIcon,
} from './DashboardViewStyle'

function DashboardView() {
  const dispatch = useDispatch()  
  const refreshTimeInSeconds = 30
  const [messageApi, contextHolder] = message.useMessage()
  const [searchFilters, setSearchFilters] = useState<{ [key: string]: string }>({
    patientId: '',
    patientName: '',
    doctor: ''
  });

  const [activeSearch, setActiveSearch] = useState<{ [key: string]: boolean }>({
    patientId: false,
    patientName: false,
    doctor: false
  });

  const [tableScroll, setTableScroll] = useState<{ x?: number; y?: number }>({ y: 0 });

  // Function to handle search input
  const handleSearch = (key: string, value: string) => {
    setSearchFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Function to toggle search input visibility
  const toggleSearch = (key: string) => {
    setActiveSearch((prev) => ({ ...prev, [key]: true }));
  };

  // Function to hide search input when blurred
  const handleBlur = (key: string) => {
    if (!searchFilters[key]) {
      setActiveSearch((prev) => ({ ...prev, [key]: false }));
    }
  };

  const resetSearchField = (key: string) => {
    setSearchFilters((prev) => ({ ...prev, [key]: '' }));
  };
  interface Doctor {
    id: string;
    name: string;
    phone: string;
    email: string;
    supervisor: string; // Maybe it exists as `isActive` instead of `status`?
    isActive: 'Y' | 'N'; // Check if this should be used instead of `status`
  }
  
  useEffect(() => {
    dispatch(getPatientAdmissions())
    dispatch(getPatients())
  }, [dispatch])

  const patientAdmissions = useSelector(selectAllPatientAdmissions)
  const [expandedKey, setExpandedKey] = useState('')
  const [openViewStates, setOpenViewStates] = useState(intialOpenViewStates())
  const patientAdmissionLoadingStatus = useSelector(selectPatientAdmissionLoadingState)

  function intialOpenViewStates() {
    let data: Record<
      string,
      {
        updateState: boolean
        configureAlertState: boolean
      }
    > = {}
    patientAdmissions?.forEach((patient) => {
      data[patient.patient.key] = {
        updateState: false,
        configureAlertState: false,
      }
    })
    return data
  }

  const patientAdmissionDataColumns: ColumnsType<PatientAdmissionViewDataType> =
    [
      {
        title: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            Id
            {activeSearch.patientId ? (
              <Input
                autoFocus
                placeholder="Search"
                value={searchFilters.patientId}
                onChange={(e) => handleSearch('patientId', e.target.value)}
                onBlur={() => handleBlur('patientId')}
                style={{ width: '100%' }}
                suffix={
                  searchFilters.patientId ? (
                    <CloseCircleOutlined
                      onClick={() => resetSearchField('patientId')}
                      style={{ cursor: 'pointer', color: '#999' }}
                    />
                  ) : null
                }
              />
            ) : (
              <SearchOutlined
                style={{ cursor: 'pointer' }}
                onClick={() => toggleSearch('patientId')}
              />
            )}
          </div>
        ),
        dataIndex: 'patientId',
        key: 'patientId',
        width: '180px',
        sorter: (a, b) => a.patientId.localeCompare(b.patientId),        

      },
      {
        title: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            Patient Name
            {activeSearch.patientName ? (
              <Input
                autoFocus
                placeholder="Search"
                value={searchFilters.patientName}
                onChange={(e) => handleSearch('patientName', e.target.value)}
                onBlur={() => handleBlur('patientName')}
                style={{ width: '150px' }}
                suffix={
                  searchFilters.patientName ? (
                    <CloseCircleOutlined
                      onClick={() => resetSearchField('patientName')}
                      style={{ cursor: 'pointer', color: '#999' }}
                    />
                  ) : null
                }
              />
            ) : (
              <SearchOutlined
                style={{ cursor: 'pointer' }}
                onClick={() => toggleSearch('patientName')}
              />
            )}
          </div>
        ),
        dataIndex: 'patientName',
        key: 'patientName',
        sorter: (a, b) => a.patientName.localeCompare(b.patientName),
        width: '180px',
      },
      {
        title: 'Patient Vitals',
        width: '120px',
        dataIndex: 'patientKey',
        key: 'patientKey',
        render: (data) => (
          <VitalStatus size='medium' patientKey={data}></VitalStatus>
        ),
      },
      {
        title: 'ECG',
        width: '120px',
        dataIndex: 'patientKey',
        key: 'patientKey',
        render: (data) => (
          <EcgStatus size='medium' patientKey={data}></EcgStatus>
        ),
      },
      {
        title: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            Doctor
            {activeSearch.doctor ? (
              <Input
                autoFocus
                placeholder="Search"
                value={searchFilters.doctor}
                onChange={(e) => handleSearch('doctor', e.target.value)}
                onBlur={() => handleBlur('doctor')}
                style={{ width: '120px' }}
                suffix={
                  searchFilters.doctor ? (
                    <CloseCircleOutlined
                      onClick={() => resetSearchField('doctor')}
                      style={{ cursor: 'pointer', color: '#999' }}
                    />
                  ) : null
                }
              />
            ) : (
              <SearchOutlined
                style={{ cursor: 'pointer' }}
                onClick={() => toggleSearch('doctor')}
              />
            )}
          </div>
        ),
        dataIndex: 'doctor',
        key: 'doctor',
        sorter: (a, b) => a.doctor.localeCompare(b.doctor),
        width: '120px',
      },
      {
        title: 'MEWS status',
        width: '120px',
        dataIndex: 'patientKey',
        key: 'patientKey',
        render: (data) => <MewsScore patientKey={data}></MewsScore>,
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            Total Alerts (Last 12 Hours)
          </div>
        ),
        width: '120px',        
        dataIndex: 'totalAlertsLast12h',
        key: 'totalAlertsLast12h',
        render: (data) => <div style={{ textAlign: 'center' }}>{data}</div>,
      },
    ]

    useEffect(() => {
      const updateScroll = () => {        
        setTableScroll(
          window.innerWidth <= 1200
            ? { x: 120 * patientAdmissionDataColumns.length, y: 0 }
            : { y: 0 }
        );
      };
      updateScroll();
      window.addEventListener('resize', updateScroll);
      return () => window.removeEventListener('resize', updateScroll);
    }, [patientAdmissionDataColumns.length]);

  const { refetch: refetchPatientMewsScoreData, data: PatientMewsScoreData } =
    useGetPatientAdmissionsMewsScoreDataQuery(
      {},
      {
        pollingInterval: refreshTimeInSeconds * 1000,
      }
    )
  const { refetch: refetchAlerts, data: totalAlertLast12h } =
    useGetTotalAlertsLast12hQuery({})

  const patientAdmissionTableData = useMemo(() => {
    const sortedDataBasedOnDoa = patientAdmissions.sort((a, b) =>
      dayjs(b.doa).diff(dayjs(a.doa))
    )
    let data: PatientAdmissionViewDataType[] = sortedDataBasedOnDoa.map(
      (patientAdmission) => {
        const name = `${patientAdmission.patient.name.title}${patientAdmission.patient.name.title.at(-1) === '.' ? '' : '.'} ${patientAdmission.patient.name.firstName} ${patientAdmission.patient.name.lastName}`
        const mewsScoreData = PatientMewsScoreData?.mewsScores?.find(
          (mewsScoreData) =>
            mewsScoreData.patientKey === patientAdmission.patient.key
        )
        const mewsScore = mewsScoreData?.mewsScore

        const alerts = totalAlertLast12h?.alerts ?? []
        const patientAlert = alerts.find(
          ({ patientKey }) => patientKey === patientAdmission.patient.key
        )
        const totalAlertsLast12h = patientAlert?.totalAlertsLast12h ?? 0

        return {
          key: patientAdmission.patient.key,
          patientKey: patientAdmission.patient.key,
          patientId: patientAdmission.patient.id,
          age: patientAdmission.patient.age,
          gender: getGender(patientAdmission.patient.sex),
          patientName: name,
          doctor: getAssignedDoctor(patientAdmission.assignedDoctorName),
          mews: mewsScore === 0 || mewsScore ? mewsScore : -1,
          totalAlertsLast12h,
        }
      }
    )
    let sortedDataBasedOnMews = [...data].sort((a, b) => b.mews - a.mews)
    return sortedDataBasedOnMews
  }, [PatientMewsScoreData, patientAdmissions, totalAlertLast12h])

 
  const filteredPatients = useMemo(() => {
    return (patientAdmissionTableData || []).filter((patient) => {
      return (
        (patient?.patientId?.toLowerCase() || "").includes(searchFilters?.patientId?.toLowerCase() || "") &&
        (patient?.patientName?.toLowerCase() || "").includes(searchFilters?.patientName?.toLowerCase() || "") &&
        (patient?.doctor?.toLowerCase() || "").includes(searchFilters?.doctor?.toLowerCase() || "")
      );
    });
  }, [searchFilters, patientAdmissionTableData]);

  const onCountdownFinish = () => {
    refetchAlerts()
    refetchPatientMewsScoreData()
  }
  const patientError = useSelector(selectErrorInPatientAdmissions)
  useEffect(() => {
    if (patientError) {
      displayMessage(patientError, 'error', messageApi)
      dispatch(errorPatientAdmissions(null))
    }
  }, [patientError])
  return (
    <>
      <Header
        title={
          <>
            <Icon
              component={DashboardIconFilled}
              style={{ fontSize: '150%' }}
            />
            <h2>Dashboard</h2>
            <CountdownButton
              size='middle'
              countDownTimeInSeconds={refreshTimeInSeconds}
              label='Auto Refreshing in'
              onCountdownFinish={onCountdownFinish}
            />
          </>
        }
      />
      {contextHolder}
      <DashboardContainer>
        <DashboardPatientAdmissionContainer>
          <DashboardStatsContainer>
            <HospitalStatsCard
              value={PatientMewsScoreData?.totalPatients}
              text='Total Patients'
              type='total'
            />
            <HospitalStatsCard
              value={patientAdmissionTableData?.length}
              text='Active Patients'
              type='active'
            />
            <HospitalStatsCard
              value={PatientMewsScoreData?.totalOnlinePatients}
              text='Online Patients'
              type='online'
            />
            <HospitalStatsCard
              value={PatientMewsScoreData?.totalCriticalPatients}
              text='Critical Patients'
              type='critical'
            />
            <HospitalStatsCard
              value={PatientMewsScoreData?.totalUrgentPatients}
              text='Urgent Patients'
              type='urgent'
            />
          </DashboardStatsContainer>
          <FullScreenContainer
            title={
              <TableTitleDiv>
                <TableTitleIcon component={PatientIcon} />
                <TableTitle>Patients</TableTitle>
              </TableTitleDiv>
            }
            titleUnderline={false}
            fullScreenOnlyTitleElements={
              <CountdownButton
                size='middle'
                countDownTimeInSeconds={refreshTimeInSeconds}
                label='Auto refreshing in'
                onCountdownFinish={onCountdownFinish}
              />
            }
          >
            <Table
              className='dashboardTable pageDataTable'
              size='middle'
              loading={patientAdmissionLoadingStatus}
              columns={patientAdmissionDataColumns}
              dataSource={filteredPatients}
              scroll={tableScroll}
              pagination={{
                position: ['bottomRight'],
                pageSizeOptions: [30, 50, 100, 200],
                defaultPageSize: 30,
              }}
              expandable={{
                expandRowByClick: true,
                expandedRowKeys: [expandedKey],
                onExpand(expanded, record) {
                  let oldExpandedElement = document.querySelector(
                    `tr[data-row-key="${expandedKey}"]`
                  ) as HTMLElement | null
                  if (oldExpandedElement)
                    oldExpandedElement.style.background = ''
                  setExpandedKey(
                    expandedKey === record.patientKey ? '' : record.patientKey
                  )
                  if (expanded) {
                    let ExpandedElement = document.querySelector(
                      `tr[data-row-key="${record.patientKey}"]`
                    ) as HTMLElement | null
                    if (ExpandedElement)
                      ExpandedElement.style.background = '#e3e3e3'
                  }
                },
                expandedRowRender: (record) => {
                  const values = {
                    configureAlertViewState:
                      openViewStates[record.patientKey]?.configureAlertState,
                    updateViewState:
                      openViewStates[record.patientKey]?.updateState,
                  }
                  return (
                    <PatientContext.Provider
                      value={{
                        ...values,
                        setOpenViewStates,
                      }}
                    >
                      <PatientTabs
                        patientKey={record.patientKey}
                        isDashboard={true}
                      />
                    </PatientContext.Provider>
                  )
                },
                expandIcon: ({ expanded, onExpand, record }) =>
                  expanded ? (
                    <DownOutlined onClick={(e) => onExpand(record, e)} />
                  ) : (
                    <RightOutlined onClick={(e) => onExpand(record, e)} />
                  ),
              }}
            />
          </FullScreenContainer>
        </DashboardPatientAdmissionContainer>
      </DashboardContainer>
    </>
  )
}

export default DashboardView

function getGender(string: any) {
  if (string === 'M'.charCodeAt(0)) return 'Male'
  else if (string === 'F'.charCodeAt(0)) return 'Female'
  return ' -'
}
function getAssignedDoctor(data: AssignedDoctorNameType) {
  if (data.firstName && data.lastName) {
    let title = data.title ?? ''
    return `${title}${data.firstName} ${data.lastName}`
  }
  return 'No Doctors Assigned'
}
